// The Quick check (docs/job-view-brief.md, section 1; Spencer, 2026-10-22): per View, the drawing beside that wall's
// full item table, no inputs, route or BOM; a scope-to-item review for the Range Estimate. A check circle per item
// sets its Checked mark (a person's act; the database refuses an AI). Opening a row is the full walk, one click away.
// The Quick check pack (Spencer, 2026-10-23; docs/quick-check-pack-brief.md): the circle flips at once and writes behind
// it, and a Checked mark never re-prices the job; a chevron opens a row to its parts inline, read-only, one circle for the
// item; an open flag is a "flag" chip beside the to-fix count, a closed one "fixed · who · when" until the check clears it.

import { Fragment, useEffect, useState } from 'react';
import type { Finding, FlagRow, ResolvedItem, WalkStop } from '@zerogap/engine';
import { refresh, rpc, type Row } from '../../../lib/api';
import { useSession } from '../../../lib/session';
import { Money } from '../../../ui/kit';
import type { JobCtx } from '../Workspace';
import { FlagState, itemFlags } from './Flags';
import { addOnShort, wallShort, type WalkModel } from './model';
import { counts, sizedName } from './Tree';

export function QuickTable({ ctx, model, wall, findings, flags, onPick, users, seats }: { ctx: JobCtx; model: WalkModel; wall: string | null; findings: Finding[]; flags: FlagRow[]; onPick: (item: ResolvedItem | { id: string }) => void; users: Row[]; seats: Row[] }) {
  const s = useSession();
  const canEdit = s.canEdit('screen.item') && !ctx.locked;
  const w = model.walls.find((x) => (wall === 'room' ? !x.view : x.view?.id === wall)) ?? model.walls[0];
  const stops = w?.stops ?? [];
  const addOnName = (id: string) => String(ctx.data.addOns.find((a) => a.id === id)?.name ?? 'add-on');
  // the circle answers at once: what was tapped shows until the row comes back from the database (change log 1)
  const [pending, setPending] = useState<Record<string, 'checked' | 'never'>>({});
  useEffect(() => {
    setPending((p) => {
      const next = { ...p };
      for (const [id, want] of Object.entries(p)) {
        const st = model.stops.find((x) => x.item?.id === id);
        if (!st || (model.stateOf(st) === 'checked') === (want === 'checked')) delete next[id];
      }
      return Object.keys(next).length === Object.keys(p).length ? p : next;
    });
  }, [model]);
  const shown = (st: WalkStop): 'checked' | 'cleared' | 'never' => (st.item && pending[st.item.id]) || model.stateOf(st);
  const toggle = async (st: WalkStop) => {
    if (!st.item) return;
    const id = st.item.id;
    const was = shown(st) === 'checked';
    setPending((p) => ({ ...p, [id]: was ? 'never' : 'checked' }));
    try {
      if (was) await rpc('uncheck_item', { p_id: id });
      else await rpc('check_item', { p_id: id, p_version: model.resOf(id).standards_version_id });
      refresh('job_item', 'job_flag');
    } catch {
      setPending((p) => {
        const n = { ...p };
        delete n[id];
        return n;
      });
    }
  };
  // a row opened to its parts (Q1), kept while the table is on screen
  const [open, setOpen] = useState<Set<string>>(() => new Set());
  const flip = (id: string) =>
    setOpen((o) => {
      const n = new Set(o);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });
  const kids = (id: string) => model.res.items.filter((i) => i.parent_item_id === id).sort((a, b) => (a.child_index ?? 0) - (b.child_index ?? 0) || a.number.localeCompare(b.number, undefined, { numeric: true }));
  const flagChips = (i: ResolvedItem) => {
    // the row speaks for the item and its parts, as the panel's count does (Iris, 2026-10-24)
    const mine = [i.id, ...kids(i.id).map((k) => k.id)].flatMap((id) => itemFlags(flags, id));
    const openOnes = mine.filter((f) => !f.resolved_at);
    const fixed = mine.filter((f) => f.resolved_at);
    return (
      <>
        {openOnes.length > 0 && (
          <span className="tag flg" title={openOnes.map((f) => f.text).join(' · ')}>
            flag
          </span>
        )}
        {fixed.length > 0 && openOnes.length === 0 && <FlagState flag={fixed[fixed.length - 1]!} users={users} seats={seats} brief />}
      </>
    );
  };
  return (
    <>
      <div className="lhead">
        <b>{w ? (w.view ? wallShort(w.name) : 'Whole room') : 'Items'}</b>
        <span className="only">
          {stops.filter((x) => x.item).length} items · the wall's full table; no inputs, route or BOM. Open a row for the full walk.
        </span>
      </div>
      <div className="qwrap">
        <table className="lt quick">
          <colgroup>
            <col style={{ width: 34 }} />
            <col style={{ width: 20 }} />
            <col style={{ width: 64 }} />
            <col />
            <col style={{ width: 150 }} />
            <col style={{ width: 86 }} />
          </colgroup>
          <thead>
            <tr>
              <th />
              <th />
              <th>No.</th>
              <th>Item · catalog item · recipe</th>
              <th>Changes · to fix</th>
              <th className="r">Price</th>
            </tr>
          </thead>
          <tbody>
            {stops.map((st) => {
              if (!st.item) {
                const g = model.res.groups.find((x) => x.kind === 'job');
                const route = st.kind === 'job_route';
                return (
                  <tr key={st.key} className="click" onClick={() => onPick({ id: st.key })}>
                    <td>
                      <i className={`ck${model.stateOf(st) === 'checked' ? ' on' : ''}`} />
                    </td>
                    <td />
                    <td className="mono">{route ? 'Route' : 'BOM'}</td>
                    <td>
                      {route ? `Job route, ${g?.steps.length ?? 0} steps` : 'Job BOM'}
                      <small className="s">typed on Job setup</small>
                    </td>
                    <td />
                    <td className="r">
                      <Money cents={route ? (g?.steps ?? []).reduce((n, x) => n + x.cents, 0) : (g?.bom ?? []).reduce((n, x) => n + x.cents, 0)} />
                    </td>
                  </tr>
                );
              }
              const i = st.item;
              const c = counts(ctx, model, i, findings, flags);
              const state = shown(st);
              const off = model.notIn(i);
              const parts = kids(i.id);
              const isOpen = parts.length > 0 && open.has(i.id);
              return (
                <Fragment key={st.key}>
                  <tr className={`click${off ? ' nob' : ''}`} onClick={() => onPick(i)}>
                    <td>
                      <button
                        type="button"
                        className={`ck${state === 'checked' ? ' on' : ''}`}
                        disabled={!canEdit}
                        aria-label={state === 'checked' ? `Uncheck ${i.number}` : `Check ${i.number}`}
                        title={state === 'checked' ? 'Checked: tap to undo' : parts.length ? `Check ${i.number} and its ${parts.length} ${parts.length === 1 ? 'part' : 'parts'}` : 'Check this item'}
                        onClick={(e) => {
                          e.stopPropagation();
                          void toggle(st);
                        }}
                      />
                    </td>
                    <td>
                      <span
                        className={`chev${isOpen ? ' open' : ''}${parts.length ? '' : ' none'}`}
                        role={parts.length ? 'button' : undefined}
                        tabIndex={parts.length ? 0 : undefined}
                        aria-label={parts.length ? (isOpen ? `Hide the parts of ${i.number}` : `Show the ${parts.length} ${parts.length === 1 ? 'part' : 'parts'} of ${i.number}`) : undefined}
                        onClick={(e) => {
                          if (!parts.length) return;
                          e.stopPropagation();
                          flip(i.id);
                        }}
                        onKeyDown={(e) => {
                          if (parts.length && (e.key === 'Enter' || e.key === ' ')) {
                            e.preventDefault();
                            e.stopPropagation();
                            flip(i.id);
                          }
                        }}
                      />
                    </td>
                    <td className="mono">{i.number}</td>
                    <td>
                      {i.name || i.catalog_item_name}
                      {i.replaces_item_id ? (
                        <span className="tag addon">for {model.res.items.find((x) => x.id === i.replaces_item_id)?.number}</span>
                      ) : (
                        i.add_on_id && <span className="tag addon">{addOnShort(addOnName(i.add_on_id))}</span>
                      )}
                      {off && <span className="tag off">not in {ctx.build?.name ?? 'this build'}</span>}
                      <small className="s">
                        {i.is_custom ? 'custom' : i.catalog_item_name}
                        {i.recipe_name ? ` · ${i.recipe_name}` : ''}
                        {parts.length > 0 && !isOpen ? ` · makes ${parts.length}` : ''}
                      </small>
                    </td>
                    <td>
                      <span className="tags">
                        {c.changes > 0 && <span className="tag chg">{c.changes}</span>}
                        {c.toFix > 0 && <span className="tag fix">{c.toFix} to fix</span>}
                        {flagChips(i)}
                      </span>
                    </td>
                    <td className="r">
                      <Money cents={i.rollup_price_cents} />
                    </td>
                  </tr>
                  {isOpen &&
                    parts.map((k) => {
                      const kc = counts(ctx, model, k, findings, flags);
                      return (
                        <tr key={k.id} className={`click part${off ? ' nob' : ''}`} onClick={() => onPick(k)} title={`${k.number}: open its panel`}>
                          <td />
                          <td />
                          <td className="mono">{k.number}</td>
                          <td>
                            {sizedName(k)}
                            <small className="s">{k.is_custom ? 'custom' : `${k.catalog_item_name}${k.recipe_name ? ` · ${k.recipe_name}` : ''}`}</small>
                          </td>
                          <td>
                            <span className="tags">
                              {kc.changes > 0 && <span className="tag chg">{kc.changes}</span>}
                              {kc.toFix > 0 && <span className="tag fix">{kc.toFix} to fix</span>}
                              {flagChips(k)}
                            </span>
                          </td>
                          <td className="r">
                            <Money cents={k.price_cents} />
                          </td>
                        </tr>
                      );
                    })}
                </Fragment>
              );
            })}
          </tbody>
        </table>
        {stops.length === 0 && <p className="only" style={{ padding: '10px 14px' }}>Nothing on this wall yet.</p>}
      </div>
    </>
  );
}

